import "../../css/GlobalStyles.css";
import { Icon } from "@iconify/react";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Event, ThemeType } from "@markit/common.types";
import { useTheme } from "../../hooks/useTheme";
import { getCanonicalMediaUrls } from "@markit/common.utils";
import DragDropList from "../Lists/DragDropList";
import MediaPreviewItem from "./MediaPreviewItem";

type MediaPreviewCarouselProps = {
  event: Event;
  hardTheme?: ThemeType;
  onSelectMedia: (index: number) => void;
  onAddMedia?: () => void;
  onRemoveMedia?: (index: number) => void;
  onReorderMedia?: (newUrls: string[]) => void;
};

// For displaying multiple media within a horizontal list carousel
const MediaPreviewCarousel = memo(function MediaPreviewCarouselFn(
  props: MediaPreviewCarouselProps
) {
  const {
    event,
    hardTheme,
    onSelectMedia,
    onAddMedia,
    onRemoveMedia,
    onReorderMedia,
  } = props;
  const { theme } = useTheme(hardTheme);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const mediaUrls = useMemo(() => getCanonicalMediaUrls(event), [event]);

  const updateFadeVisibility = useCallback(() => {
    const node = scrollContainerRef.current;
    if (!node) {
      return;
    }
    const maxScrollLeft = node.scrollWidth - node.clientWidth;
    const hasOverflow = maxScrollLeft > 1;
    setShowLeftFade(hasOverflow && node.scrollLeft > 1);
    setShowRightFade(hasOverflow && node.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    updateFadeVisibility();
  }, [mediaUrls.length, updateFadeVisibility]);

  useEffect(() => {
    window.addEventListener("resize", updateFadeVisibility);
    return () => {
      window.removeEventListener("resize", updateFadeVisibility);
    };
  }, [updateFadeVisibility]);

  const fadeStartColor =
    hardTheme === ThemeType.Dark ? "rgba(0,0,0,0.9)" : "rgba(255,255,255,0.95)";
  const fadeEndColor =
    hardTheme === ThemeType.Dark ? "rgba(0,0,0,0)" : "rgba(255,255,255,0)";

  const styles = {
    root: {
      position: "relative" as const,
    },
    scrollContainer: {
      gap: 10,
      overflowX: "auto" as const,
      scrollbarWidth: "none" as const,
      msOverflowStyle: "none" as const,
    },
    addTile: {
      width: 70,
      height: 70,
      borderRadius: 12,
      border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
      display: "grid",
      cursor: "pointer",
      flexShrink: 0,
    },
    fadeLeft: {
      position: "absolute" as const,
      left: 0,
      top: 0,
      bottom: 0,
      width: 24,
      pointerEvents: "none" as const,
      background: `linear-gradient(90deg, ${fadeStartColor} 0%, ${fadeEndColor} 100%)`,
      zIndex: 2,
    },
    fadeRight: {
      position: "absolute" as const,
      right: 0,
      top: 0,
      bottom: 0,
      width: 24,
      pointerEvents: "none" as const,
      background: `linear-gradient(270deg, ${fadeStartColor} 0%, ${fadeEndColor} 100%)`,
      zIndex: 2,
    },
  };

  return (
    <div style={styles.root}>
      <div
        ref={scrollContainerRef}
        className="AlignedRow HideScrollbar"
        style={styles.scrollContainer}
        onScroll={updateFadeVisibility}
      >
        {onReorderMedia ? (
          <DragDropList
            items={mediaUrls.map((mediaURL, index) => ({
              id: `media-${index}`,
              value: mediaURL,
            }))}
            handleChange={(newItems: { id: string; value: string }[]) =>
              onReorderMedia(newItems.map((item) => item.value))
            }
            isHorizontal
          >
            {(
              item: { id: string; value: string },
              index: number,
              dragHandleProps: Record<string, unknown> | undefined
            ) => (
              <MediaPreviewItem
                mediaUrl={item.value}
                aspectRatio={onAddMedia ? undefined : event.mediaAspectRatio}
                index={index}
                size={70}
                dragHandleProps={dragHandleProps}
                onSelectMedia={onSelectMedia}
                onRemoveMedia={onRemoveMedia}
              />
            )}
          </DragDropList>
        ) : (
          mediaUrls.map((mediaURL, index) => (
            <MediaPreviewItem
              key={`${mediaURL}-${index}`}
              mediaUrl={mediaURL}
              aspectRatio={onAddMedia ? undefined : event.mediaAspectRatio}
              index={index}
              size={70}
              onSelectMedia={onSelectMedia}
              onRemoveMedia={onRemoveMedia}
            />
          ))
        )}
        {onAddMedia ? (
          <div
            className="Centering"
            style={styles.addTile}
            onClick={onAddMedia}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <Icon
              icon="ion:add"
              height={52}
              style={{ color: theme.PrimaryText.color }}
            />
          </div>
        ) : null}
      </div>
      {showLeftFade ? <div style={styles.fadeLeft} /> : null}
      {showRightFade ? <div style={styles.fadeRight} /> : null}
    </div>
  );
});

export default MediaPreviewCarousel;
